import { FeedbackMessage } from 'components/secondaryMarket/shared/FeedbackMessage';
import { Label } from 'components/secondaryMarket/shared/Label';
import { ButtonsGroup } from 'components/shared/ButtonsGroup/ButtonsGroup';
import { MonoDatePicker } from 'components/shared/DatePicker';
import { useSPIntl } from 'hooks/useIntl';
import { FC, useMemo } from 'react';
import {
  availableTradeDateTypes,
  type TradeDateType,
} from 'state/secondaryMarket/cpOrder/cpOrderForm/input/cpOrderInputForm.model';
import type { CpOrderSettlement } from 'state/secondaryMarket/cpOrder/referential/client/client.model';
import type { WorkspaceType } from 'state/workspaces/model';

import { Block } from '../../../shared/Blocks/Block';
import { DateSelector } from '../../../shared/components/DateSelector';
import { SettlementTypeSelector } from '../../../shared/components/SettlementTypeSelector';
import { VerticalFormGroup } from '../../../shared/VerticalFormGroup';
import { CutOffInfos, RefreshedCutOffInfos } from './cutoff';

const fieldIds: string[] = ['valuationDate', 'settlementDate', 'settlementType', 'tradeDateType'];

type BookingInformationProps = {
  tabId: string;
  workspaceType: WorkspaceType;
  className?: string;
  cutOffTime: string | undefined;
  cutOffZoneId: string | undefined;
  nextCutOff: Date | undefined;
  settlementDate: string | undefined;
  firstSettlementDate: string | undefined;
  settlementHolidays: ReadonlyArray<string> | undefined;
  valuationDate: string | undefined;
  tradeDateType: TradeDateType;
  availableValuationDates: ReadonlyArray<string> | undefined;
  availableSettlementTypes: ReadonlyArray<CpOrderSettlement> | undefined;
  settlementType: string | undefined;
  cobDate: string | undefined;
  isLoadingSettlement: boolean;
  isFrozen: boolean;
  onSettlementDateChanged: (value: Nullable<string>) => void;
  onValuationDateChanged: (value: Nullable<string>) => void;
  onSettlementTypeChanged: (value: string | undefined) => void;
  onTradeDateTypeChanged: (value: TradeDateType) => void;
};

export const CpOrderBookingInformation: FC<BookingInformationProps> = ({
  className,
  cutOffTime,
  cutOffZoneId,
  nextCutOff,
  settlementDate,
  firstSettlementDate,
  availableValuationDates,
  valuationDate,
  cobDate,
  settlementHolidays,
  tradeDateType,
  availableSettlementTypes,
  isLoadingSettlement,
  settlementType,
  isFrozen,
  onSettlementDateChanged,
  onValuationDateChanged,
  onTradeDateTypeChanged,
  onSettlementTypeChanged,
}) => {
  const { formatDisplayedDate } = useSPIntl();
  const blackoutSettlementDates = useMemo<string[]>(() => settlementHolidays?.slice() ?? [], [settlementHolidays]);
  const hasIncoherentSettlementDate = settlementDate && blackoutSettlementDates.some(x => x === settlementDate);

  return (
    <Block
      className={className}
      label="secondaryMarket.bookingInformation.label.bookingInformation"
      id="booking-information"
      fieldIds={fieldIds}
    >
      <div>
        <div className="row">
          <VerticalFormGroup
            inputId="CuttOffTime"
            label="secondaryMarket.bookingInformation.cuttOffTime"
            className="col-md-6"
          >
            {cutOffTime !== undefined &&
              cutOffZoneId !== undefined &&
              (nextCutOff !== undefined ? (
                <RefreshedCutOffInfos cutOffTime={cutOffTime} cutOffZoneId={cutOffZoneId} nextCutOff={nextCutOff} />
              ) : (
                <CutOffInfos cutOffTime={cutOffTime} cutOffZoneId={cutOffZoneId} />
              ))}
          </VerticalFormGroup>
          <VerticalFormGroup inputId="CobDate" label="secondaryMarket.bookingInformation.cobDate" className="col-md-6">
            {cobDate !== undefined && <Label id="CobDate" value={formatDisplayedDate(cobDate)} e2e="cob-date" />}
          </VerticalFormGroup>
        </div>
        <div className="row">
          <VerticalFormGroup
            inputId="valuationDate"
            label="secondaryMarket.bookingInformation.valuationDate"
            className="col-md-6"
          >
            {valuationDate !== undefined && availableValuationDates !== undefined && (
              <DateSelector
                selectedValue={valuationDate}
                availableValues={availableValuationDates}
                onValueChanged={onValuationDateChanged}
                isDisabled={isFrozen}
                id="valuationDate"
                e2e="valuation-date"
                placeholder="secondaryMarket.bookingInformation.valuationDate"
              />
            )}
          </VerticalFormGroup>
          <VerticalFormGroup
            inputId="settlementDate"
            label="secondaryMarket.bookingInformation.settlementDate"
            className="col-md-6"
          >
            {settlementDate !== undefined && (
              <>
                <MonoDatePicker
                  id="SettlementDate"
                  value={settlementDate}
                  minDate={firstSettlementDate}
                  allowWeekend={false}
                  blackoutDates={blackoutSettlementDates}
                  onChange={onSettlementDateChanged}
                  datePresetTranslations={[]}
                  datePresets={[]}
                  isClearable={false}
                  disabled={isFrozen}
                  e2e="settlement-date"
                />
                {hasIncoherentSettlementDate && (
                  <FeedbackMessage labelKey="secondaryMarket.errors.settlementDate.incoherent" color="info" />
                )}
              </>
            )}
          </VerticalFormGroup>
        </div>
        <div className="row">
          <VerticalFormGroup
            inputId="settlementType"
            label="secondaryMarket.bookingInformation.settlementType"
            className="col-md-6"
          >
            <SettlementTypeSelector
              isFrozen={isFrozen}
              availableSettlementTypes={availableSettlementTypes}
              settlementType={settlementType}
              isLoadingSettlement={isLoadingSettlement}
              onSettlementTypeChanged={onSettlementTypeChanged}
            />
          </VerticalFormGroup>
          <VerticalFormGroup
            inputId="tradeDateType"
            label="secondaryMarket.bookingInformation.tradeDate"
            className="col-md-6"
          >
            <ButtonsGroup
              id="tradeDateType"
              className="w-100"
              items={availableTradeDateTypes}
              selectedItems={tradeDateType}
              onSelect={items => onTradeDateTypeChanged(items[0])}
              isRadio
              translationPrefix="form.tradeDateType"
              isDisabled={isFrozen}
              e2e="trade-date-type-buttons"
            />
          </VerticalFormGroup>
        </div>
      </div>
    </Block>
  );
};

CpOrderBookingInformation.displayName = 'CpOrderFormBookingInformation';
